import React, { Fragment, useMemo, useCallback } from 'react';
import { Form } from 'react-final-form';
import { Box, Grid, Paper } from '@mui/material';
import { TextField } from 'mui-rff';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
//
import Title from 'components/common/Title';
import Loader from 'components/common/Loader';
import ButtonSubmit from 'components/common/ButtonSubmit';
import {
  composeValidators,
  required,
  isNumber,
  isNumberAndNegative,
  maxValue,
} from 'utils/validate';
import { TRANSACTIONS_HISTORY_COLUMN_DEFS } from 'utils/tables/columns/transactionsHistory';
import {
  useGetGatewayBudgetQuery,
  useGetGatewayTransactionsQuery,
  useUpdateGatewayBudgetMutation,
} from '../../../../store/api/transaction.api';

const FlexGridItem = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'flex-end',
  marginBottom: '30px',
}));

const maxCommissionValue = maxValue(100);

const Gateway = ({ id, onFormStateChanged, editable }) => {
  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const { data: budgetData = { budget: 0, remainingBudget: 0 } } =
    useGetGatewayBudgetQuery(id);
  const budgetForm = useMemo(
    () => ({ ...budgetData, commission: 0, deposit: 0 }),
    [budgetData],
  );
  const { data: transactions = [] } = useGetGatewayTransactionsQuery(
    budgetData.wlid,
    {
      skip: !budgetData.wlid,
    },
  );
  const [updateBudget] = useUpdateGatewayBudgetMutation();

  const decorators = useMemo(
    () => [
      ({ subscribe }) =>
        subscribe(({ dirty }) => onFormStateChanged(dirty), { dirty: true }),
    ],
    [onFormStateChanged],
  );

  // actions
  const onSubmit = useCallback(
    async (values) => {
      try {
        await updateBudget({ id, ...values }).unwrap();
        enqueueSnackbar('Success!', { variant: 'success' });
      } catch (e) {
        enqueueSnackbar('Failed to top up budget', { variant: 'error' });
      }
    },
    [enqueueSnackbar, updateBudget, id],
  );

  return (
    <Fragment>
      <Paper>
        <Form
          initialValues={budgetForm}
          decorators={decorators}
          onSubmit={onSubmit}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Loader isPending={submitting} />
              <Paper style={{ padding: 16 }} component={Grid} container>
                <FlexGridItem size={{ xs: 6 }}>
                  <TextField
                    name='balance'
                    label='Total Balance'
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                    disabled
                  />
                </FlexGridItem>
                <FlexGridItem size={{ xs: 6 }}>
                  <TextField
                    name='remainingBudget'
                    label='Remaining Balance'
                    slotProps={{ inputLabel: { shrink: true } }}
                    style={{ marginLeft: '20px' }}
                    fullWidth
                    disabled
                  />
                </FlexGridItem>
                <FlexGridItem size={{ xs: 6 }}>
                  <TextField
                    name='commission'
                    label='Commission (%)'
                    slotProps={{ inputLabel: { shrink: true } }}
                    disabled={!editable}
                    fieldProps={{
                      validate: composeValidators(
                        required,
                        isNumber,
                        maxCommissionValue,
                      ),
                    }}
                    fullWidth
                  />
                </FlexGridItem>
                <FlexGridItem size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    name='deposit'
                    label='Top Up Balance'
                    slotProps={{ inputLabel: { shrink: true } }}
                    fieldProps={{
                      validate: composeValidators(
                        required,
                        isNumberAndNegative,
                      ),
                    }}
                    style={{ marginLeft: '20px' }}
                    disabled={!editable}
                    fullWidth
                  />
                </FlexGridItem>
                <FlexGridItem size={{ xs: 6 }}>
                  <ButtonSubmit
                    name='add'
                    type='submit'
                    disabled={!editable}
                    style={{ marginLeft: '20px' }}
                  />
                </FlexGridItem>
              </Paper>
            </form>
          )}
        />
      </Paper>
      <Paper>
        <div style={{ width: '100%', marginTop: '15px' }}>
          <Box sx={{ pt: 2, px: 2 }}>
            <Title>Transaction History</Title>
          </Box>
          <Box sx={{ pt: 2, px: 2 }}>
            <DataGrid
              rows={transactions}
              columns={TRANSACTIONS_HISTORY_COLUMN_DEFS}
            />
          </Box>
        </div>
      </Paper>
    </Fragment>
  );
};

export default Gateway;
